// Командная палитра (Ctrl+K): поиск по всем командам всех функций и по структуре документа
// (разделы, рисунки, таблицы, формулы, приложения - переход). Недавние команды - сверху.
import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { registry, type CommandSpec } from '../../core/registry';
import { ctx, runCommand } from '../../core/ctx';
import { appStore } from '../../core/store';
import { readPref, writePref } from '../../core/store';
import { currentIndex, gotoNode } from '../../core/docIndex';
import { STRUCT_KINDS } from '../../core/schema/constants';
import { uiStore, useUi } from '../../ui/uiState';
import { Icon } from '../../ui/icons';
import { prettyShortcut } from '../../ui/controls';

/** Дополнительные слова для поиска команд (синонимы, как ищут студенты). */
const KEYWORDS: Record<string, string> = {
  'insert.figure': 'рисунок картинка изображение скриншот фото image',
  'insert.table': 'таблица table',
  'insert.listing': 'листинг код программа code',
  'math.formula': 'формула уравнение equation latex',
  'math.inline': 'формула в строке',
  'refs.cite': 'ссылка источник литература цитата',
  'refs.xref': 'ссылка перекрёстная на рисунок таблицу',
  'refs.footnote': 'сноска',
  'sto.check': 'проверка проверить ошибки нормоконтроль',
  'sto.meta': 'титул титульный лист тема преподаватель студент данные',
  'file.exportDocx': 'word ворд сохранить скачать',
  'file.exportPdf': 'pdf пдф',
  'block.h1': 'заголовок раздел глава', 'block.h2': 'заголовок подраздел', 'block.h3': 'заголовок пункт',
  'list.dash': 'список маркированный дефис', 'list.digit': 'список нумерованный', 'list.letter': 'список буквенный',
  'view.theme': 'тёмная темная ночь dark', 'edit.find': 'поиск найти', 'edit.replace': 'замена',
  'view.focus': 'фокус без отвлечений', 'file.versions': 'история версии откат', 'insert.pageBreak': 'разрыв страница',
};

const norm = (s: string) => s.toLowerCase().replace(/ё/g, 'е');

interface Item { key: string; icon?: string; title: string; desc?: string; shortcut?: string; group: string; run: () => void; testid?: string }

function recent(): string[] { try { return JSON.parse(readPref('recentCmds', '[]') || '[]'); } catch { return []; } }
function pushRecent(id: string) { writePref('recentCmds', JSON.stringify([id, ...recent().filter((x) => x !== id)].slice(0, 8))); }

function commandItems(): Item[] {
  const hasProject = !!appStore.getState().projectId;
  return registry.commands.list()
    .filter((c) => c.id !== 'app.palette')
    .filter((c) => hasProject || c.id.startsWith('app.'))
    .filter((c) => (c.isEnabled ? safe(() => c.isEnabled!(ctx)) : true))
    .map((c: CommandSpec) => ({ key: 'c:' + c.id, icon: c.icon, title: c.title, desc: c.description, shortcut: c.shortcut,
      group: 'Команды', testid: 'pal-' + c.id, run: () => { pushRecent(c.id); runCommand(c.id); } }));
}
function safe(f: () => boolean) { try { return f(); } catch { return false; } }

function outlineItems(): Item[] {
  const e = appStore.getState().editor;
  if (!e) return [];
  const ix = currentIndex(e);
  const out: Item[] = [];
  for (const en of ix.entries) {
    let title = '';
    let icon = 'paragraph';
    if (en.type === 'heading') { title = `${en.num?.num ? en.num.num + ' ' : ''}${en.text}`; icon = 'h' + en.attrs.level; }
    else if (en.type === 'structHeading') { title = STRUCT_KINDS[en.attrs.kind] || en.attrs.kind; icon = 'bookmark'; }
    else if (en.type === 'appendix') { title = `Приложение ${en.num?.letter || ''} ${en.node.firstChild?.textContent || ''}`; icon = 'attach'; }
    else if (en.type === 'figure') { title = `Рисунок ${en.num?.num || ''} ${en.text}`; icon = 'image'; }
    else if (en.type === 'stoTable') { title = `Таблица ${en.num?.num || ''} ${en.node.firstChild?.type.name === 'caption' ? en.node.firstChild.textContent : ''}`; icon = 'table'; }
    else if (en.type === 'listing') { title = `Листинг ${en.num?.num || ''} ${en.text}`; icon = 'braces'; }
    else if (en.type === 'formula' && en.num?.num) { title = `Формула (${en.num.num}) ${en.attrs.latex || ''}`; icon = 'formula'; }
    else continue;
    out.push({ key: 'n:' + en.id, icon, title: title.trim(), group: 'Перейти', run: () => gotoNode(e, en.id) });
  }
  return out;
}

function score(it: Item, words: string[]): number {
  const id = it.key.slice(2);
  const hay = norm(`${it.title} ${it.desc || ''} ${KEYWORDS[id] || ''} ${id}`);
  const t = norm(it.title);
  let s = 0;
  for (const w of words) {
    const i = hay.indexOf(w);
    if (i < 0) return -1;
    s += t.startsWith(w) ? 30 : t.includes(' ' + w) ? 20 : t.includes(w) ? 12 : 4;
  }
  return s + (it.group === 'Команды' ? 5 : it.group === 'Отчёты' ? -3 : 0);
}

export function openPalette() { uiStore.getState().set({ paletteOpen: true }); }

export function CommandPalette() {
  const open = useUi((s) => s.paletteOpen);
  if (!open) return null;
  return <PaletteBody />;
}

/** Другие отчёты - «Перейти к отчёту» (список загружается при открытии палитры). */
function useProjectItems(): Item[] {
  const [items, setItems] = useState<Item[]>([]);
  useEffect(() => {
    let alive = true;
    const cur = appStore.getState().projectId;
    ctx.api.listProjects().then((r) => {
      if (!alive) return;
      setItems(r.projects.filter((p) => p.id !== cur).slice(0, 40).map((p) => ({
        key: 'p:' + p.id, icon: 'file', title: p.name, desc: [p.title, p.discipline].filter(Boolean).join(' · '), group: 'Отчёты',
        run: () => { location.hash = '#/p/' + encodeURIComponent(p.id); },
      })));
    }).catch(() => { /* сервер недоступен */ });
    return () => { alive = false; };
  }, []);
  return items;
}

function PaletteBody() {
  const [q, setQ] = useState('');
  const [sel, setSel] = useState(0);
  const listRef = useRef<HTMLDivElement>(null);
  const projects = useProjectItems();
  const all = useMemo(() => ({ cmds: commandItems(), outline: outlineItems(), projects }), [projects]);
  const items = useMemo(() => {
    const words = norm(q).split(/\s+/).filter(Boolean);
    if (!words.length) {
      const rec = recent().map((id) => all.cmds.find((c) => c.key === 'c:' + id)).filter(Boolean) as Item[];
      const recSet = new Set(rec.map((r) => r.key));
      return [...rec.map((r) => ({ ...r, group: 'Недавние' })), ...all.cmds.filter((c) => !recSet.has(c.key))];
    }
    return [...all.cmds, ...all.outline, ...all.projects].map((it) => [it, score(it, words)] as const).filter(([, s]) => s >= 0)
      .sort((a, b) => b[1] - a[1]).slice(0, 60).map(([it]) => it);
  }, [q, all]);
  useEffect(() => { setSel(0); }, [q]);
  useEffect(() => {
    (listRef.current?.querySelector('.pal-item.is-sel') as HTMLElement | null)?.scrollIntoView({ block: 'nearest' });
  }, [sel]);
  const close = () => { uiStore.getState().set({ paletteOpen: false }); };
  const exec = (it?: Item) => { if (!it) return; close(); setTimeout(() => it.run(), 0); };
  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setSel((s) => Math.min(items.length - 1, s + 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setSel((s) => Math.max(0, s - 1)); }
    else if (e.key === 'PageDown') { e.preventDefault(); setSel((s) => Math.min(items.length - 1, s + 8)); }
    else if (e.key === 'PageUp') { e.preventDefault(); setSel((s) => Math.max(0, s - 8)); }
    else if (e.key === 'Enter') { e.preventDefault(); exec(items[sel]); }
    else if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); close(); appStore.getState().editor?.commands.focus(); }
  };
  let lastGroup = '';
  return createPortal(
    <div className="palette-back" onMouseDown={(e) => { if (e.target === e.currentTarget) close(); }}>
      <div className="palette" role="dialog" aria-label="Командная палитра" data-testid="palette" onKeyDown={onKey}>
        <div className="palette-input">
          <Icon name="search" size={18} />
          <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Команда или раздел документа… (например: «рисунок», «pdf», «выводы»)"
            data-testid="palette-input" aria-label="Поиск команды" aria-controls="palette-list"
            aria-activedescendant={items[sel] ? 'pal-' + sel : undefined} />
          <span className="kbd-group"><kbd>Esc</kbd></span>
        </div>
        <div className="palette-list" id="palette-list" role="listbox" ref={listRef}>
          {!items.length && <div className="palette-empty">Ничего не найдено</div>}
          {items.map((it, i) => {
            const head = it.group !== lastGroup ? <div className="pal-group" key={'g' + i}>{it.group}</div> : null;
            lastGroup = it.group;
            return [head,
              <div key={it.key} id={'pal-' + i} role="option" aria-selected={i === sel} data-testid={it.testid}
                className={'pal-item' + (i === sel ? ' is-sel' : '')} onMouseMove={() => setSel(i)} onClick={() => exec(it)}>
                <span className="pal-ico"><Icon name={it.icon} size={16} /></span>
                <span className="pal-text"><span className="pal-title">{it.title}</span>{it.desc && <span className="pal-desc">{it.desc}</span>}</span>
                {it.shortcut && <span className="pal-key">{prettyShortcut(it.shortcut)}</span>}
              </div>];
          })}
        </div>
        <div className="palette-foot"><span><kbd>↑</kbd><kbd>↓</kbd> выбор</span><span><kbd>Enter</kbd> выполнить</span><span>F1 - справка</span></div>
      </div>
    </div>, document.body);
}
